import { NgZone } from "@angular/core";
import { AccessCatalogEntry } from "./services/access-catalog.service";
import { AccessAuditEntry } from "./services/access-audit.service";
import { RoleTag, resolveRoleTag, roleTagTextColor } from "src/app/shared/role-tags";

/**
 * Diferença entre os acessos atuais e os escolhidos, só dentro do catálogo.
 * Papéis fora do catálogo (ex.: guest) não são tocados.
 */
export function diffManagedRoles(
  current: Array<string>,
  next: Array<string>,
  catalog: Array<AccessCatalogEntry>,
): { add: Array<string>; remove: Array<string> } {
  const managed = new Set(catalog.map((c) => c.key));
  const before = (current || []).filter((r) => managed.has(r));
  const after = (next || []).filter((r) => managed.has(r));
  return {
    add: after.filter((r) => !before.includes(r)),
    remove: before.filter((r) => !after.includes(r)),
  };
}

export const ACCESS_GROUP_LABELS: Record<string, string> = {
  esp: "ESP",
  mcp: "MCP",
  smartreview: "Smart Review",
};

export function groupCatalog(catalog: Array<AccessCatalogEntry>): Array<{ group: string; label: string; entries: Array<AccessCatalogEntry> }> {
  const groups: Array<{ group: string; label: string; entries: Array<AccessCatalogEntry> }> = [];
  catalog.forEach((entry) => {
    let g = groups.find((x) => x.group === entry.group);
    if (!g) {
      g = { group: entry.group, label: ACCESS_GROUP_LABELS[entry.group] ?? entry.group, entries: [] };
      groups.push(g);
    }
    g.entries.push(entry);
  });
  return groups;
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  "roles.grant": "Acesso concedido",
  "roles.revoke": "Acesso revogado",
  "user.area": "Área alterada",
  "user.create": "Usuário criado",
  "user.delete": "Usuário excluído",
  "area.create": "Área criada",
  "area.rename": "Área renomeada",
  "area.tribe": "Área movida de tribe",
  "area.restricted": "Receitas restritas alteradas",
  "area.delete": "Área excluída",
  "tribe.create": "Tribe criada",
  "tribe.rename": "Tribe renomeada",
  "tribe.delete": "Tribe excluída",
};

export function auditActionOptions(): Array<{ value: string; label: string }> {
  return Object.entries(AUDIT_ACTION_LABELS).map(([value, label]) => ({ value, label }));
}

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

/**
 * Rótulo do evento: "area.tribe" sem tribe de origem (ou de destino) não é uma mudança de tribe,
 * é a área sendo vinculada (ou desvinculada) — caso de todas as áreas na migração.
 */
export function auditEventLabel(action: string, details?: Record<string, any> | null): string {
  if (action === "area.tribe") {
    if (!details?.["from"] && details?.["to"]) return "Área vinculada à tribe";
    if (details?.["from"] && !details?.["to"]) return "Área desvinculada da tribe";
  }
  return auditActionLabel(action);
}

/** Quem alterou; a conta técnica da migração aparece como "Migração". */
export function auditActorLabel(actor: string): string {
  return actor === "migration-gestao-usuarios" ? "Migração" : actor;
}

/** Tag do "O que mudou": concedido em verde, revogado/excluído em vermelho, criado em azul, demais em azul-claro. */
export function auditActionTag(action: string, details?: Record<string, any> | null): RoleTag {
  let color = "color-01";
  if (action === "roles.grant") color = "color-10";
  else if (action === "roles.revoke" || action.endsWith(".delete")) color = "color-07";
  else if (action.endsWith(".create")) color = "color-02";
  const textColor = roleTagTextColor(color);
  const label = auditEventLabel(action, details);
  return textColor ? { label, color, textColor } : { label, color };
}

/** Acessos concedidos/revogados como tags (mesmo nome e cor do perfil); vazio para os demais eventos. */
export function auditRoleTags(entry: AccessAuditEntry): Array<RoleTag & { key: string }> {
  if (entry.action !== "roles.grant" && entry.action !== "roles.revoke") return [];
  const roles: Array<string> = ((entry.details ?? {}) as Record<string, any>)["roles"] ?? [];
  return roles.map((key) => ({ key, ...resolveRoleTag(key) }));
}

/**
 * Data final do recorte da auditoria. O backend lê "YYYY-MM-DD" como meia-noite UTC e inclui o dia somando 24 h, mas o
 * período da tela é o dia LOCAL do navegador: no Brasil (UTC-3), das 21:00 à meia-noite o UTC já virou o dia e o que foi
 * gravado nessa janela ficava de fora de "até hoje". Mandar o dia seguinte cobre essas horas (sem depender do fuso
 * do navegador: a conta é só de calendário). O custo é incluir, no fim do recorte, as primeiras horas do dia seguinte.
 */
export function auditPeriodEnd(dateTo?: string): string | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateTo || "");
  if (!match) return undefined;
  const [year, month, day] = match.slice(1).map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  if (isNaN(next.getTime())) return undefined;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}
/** "05/10/2026 20:31" — curto o bastante para não cortar na coluna. */
export function formatAuditWhen(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const orNone = (value: unknown): string => (value === null || value === undefined || value === "" ? "Sem área" : String(value));

/** Texto curto do que mudou, para a coluna "Detalhes". */
export function describeAudit(entry: AccessAuditEntry): string {
  const d = (entry.details ?? {}) as Record<string, any>;
  switch (entry.action) {
    case "roles.grant":
    case "roles.revoke":
      return (d["roles"] ?? []).map((r: string) => resolveRoleTag(r).label).join(", ");
    case "user.area":
      return `${orNone(d["from"])} → ${orNone(d["to"])}`;
    case "area.tribe":
      // vinculada/desvinculada: só a tribe (o rótulo já diz o que aconteceu); movida: de → para
      if (!d["from"] || !d["to"]) return `Tribe ${d["to"] || d["from"] || ""}`.trim();
      return `${d["from"]} → ${d["to"]}`;
    case "area.rename":
    case "tribe.rename":
      return `${d["from"]} → ${d["to"]}`;
    case "area.restricted":
      return d["to"] ? "restritas à área" : "todas as receitas";
    case "user.create":
      return d["area"] ? `área ${d["area"]}` : "";
    default:
      return "";
  }
}

/**
 * Altura da tabela de uma aba para a página caber na tela sem rolagem: com altura fixa o po-table
 * prende o cabeçalho e rola só as linhas. Mede quanto falta (ou sobra) entre o fim da aba e o fim
 * da área rolável da página e corrige a altura; refaz a conta quando a aba muda de tamanho (filtros
 * ativos, barra de seleção, "Carregar mais") ou quando a área rolável muda com a janela.
 */
export class TableHeightFitter {
  height: number;

  private observer?: ResizeObserver;

  constructor(
    private readonly host: HTMLElement,
    private readonly zone: NgZone,
    private readonly minHeight = 280,
  ) {
    this.height = Math.max(minHeight, window.innerHeight - 400);
  }

  start(): void {
    // a aba (filtros, seleção, "Carregar mais") e a área rolável (o PO-UI a redimensiona com a janela)
    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(() => this.fit());
      this.observer.observe(this.host);
      const container = scrollParent(this.host);
      if (container) this.observer.observe(container);
    }
    // fora do ciclo de deteccao atual: medir e mudar a altura durante o ngAfterViewInit gera NG0100
    Promise.resolve().then(() => this.fit());
  }

  stop(): void {
    this.observer?.disconnect();
  }

  private fit(): void {
    const container = scrollParent(this.host);
    if (!container) return;
    const paddingBottom = parseFloat(getComputedStyle(container).paddingBottom) || 0;
    const slack = container.getBoundingClientRect().bottom - paddingBottom - this.host.getBoundingClientRect().bottom;
    const next = Math.min(window.innerHeight, Math.max(this.minHeight, Math.floor(this.height + slack)));
    if (Math.abs(next - this.height) >= 1) {
      this.zone.run(() => (this.height = next));
    }
  }
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}
